import { useContext, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useParams } from "react-router-dom";
import { Box, lighten, Stack, Typography, useTheme, darken } from "@mui/material";
import { Person } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import format from "date-fns/format";

import { ReactComponent as TaskSvg } from "assets/svg/task.svg";

import { ButtonContained } from "components/button/ButtonContained";

import { iconColor, statusBgColor, TaskActivityResponse } from "user-journey/consts/journeyActivity";
import { ActivityStatus } from "user-journey/consts/journeyActivity";

import { apiClient } from "utils/apiClient";
import { getErrorMessage } from "utils/errorUtils";
import { UserLevelContext } from "utils/contexts/user-level";
import { getInvertColor } from "utils/themeUtils";

import { getToken } from "auth/helpers/authApi";

import { greyColors } from "theme/colors";

import {
	ActivityCard,
	ActivityHeader,
	ActivityStatusChip,
	CalendarIcon,
	LockedSection,
} from "../JourneyActivity.styled";

export const TaskActivityCard = ({
	activity,
	fetchUserJourney,
	refetchExternalTasks,
}: {
	activity: TaskActivityResponse;
	fetchUserJourney?: () => Promise<void>;
	refetchExternalTasks?: () => Promise<void>;
}) => {
	const { pathname } = useLocation();
	const params = useParams();

	const { formatMessage } = useIntl();

	const { enqueueSnackbar } = useSnackbar();

	const theme = useTheme();

	const { fetchUserLevel } = useContext(UserLevelContext);
	const [completeLoading, setCompleteLoading] = useState(false);

	const completeHandler = async () => {
		const isExternal = pathname.startsWith("/external");
		const token = getToken();
		setCompleteLoading(true);

		try {
			const url = isExternal
				? `user-tasks/external/${params.base64Email}/complete/${activity.userTaskId}`
				: `user-tasks/${activity.userTaskId}/complete`;
			await apiClient(url, {
				token,
				method: "POST",
			});

			if (fetchUserJourney && !isExternal) {
				await fetchUserJourney();
			}
			if (isExternal && refetchExternalTasks) {
				await refetchExternalTasks();
			}

			enqueueSnackbar(
				formatMessage({
					id: "DASHBOARD.employee-journey.activity_type.task.complete.success",
					defaultMessage: "Task completed",
				}),
				{
					variant: "success",
				},
			);

			if (!isExternal) {
				await fetchUserLevel();
			}
		} catch (error) {
			const errorJson = (error as any)?.json;
			let errorMessage = formatMessage({
				id: "DASHBOARD.employee-journey.create_error",
			});
			if (errorJson.code) {
				errorMessage = getErrorMessage(errorJson.code);
			}
			enqueueSnackbar(errorMessage, { variant: "error" });
		} finally {
			setCompleteLoading(false);
		}
	};

	const isLight = getInvertColor(theme.palette.primary.main) === "#3C4255";

	return (
		<ActivityCard
			type={activity.type}
			status={activity.status}
			padding="0"
			bgcolor={
				[ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(activity.status)
					? isLight
						? theme.palette.primary["200"]
						: lighten(theme.palette.primary.main, 0.3)
					: statusBgColor[activity.status]
			}
			sx={{
				...([ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(activity.status) && {
					border: (theme) =>
						isLight
							? `1px solid ${theme.palette.primary["400"]}`
							: `1px solid ${lighten(theme.palette.primary.main, 0.1)}`,
				}),
			}}
		>
			<ActivityStatusChip status={activity.status} />
			<Box
				display="flex"
				flexDirection="column"
				alignItems="center"
				flex="1"
				gap="0.8rem"
				p="3.2rem 1.6rem 0 1.6rem"
				sx={{ width: "100%" }}
			>
				<ActivityHeader
					sx={{
						"& svg .organization-color": {
							fill: (theme) =>
								isLight ? theme.palette.primary["200"] : lighten(theme.palette.primary.main, 0.3),
						},
						"& svg .organization-color.darken": {
							fill: (theme) =>
								isLight ? darken(theme.palette.primary.main, 0.5) : theme.palette.primary.main,
						},
						"& svg .organization-color.lighten": {
							fill: (theme) => theme.palette.primary["400"],
						},
						"& svg .line": {
							fill: (theme) => theme.palette.grey["700"],
						},
						"& svg .shadow": {
							fill: (theme) => theme.palette.grey["600"],
						},
						"& svg .remove-color": {
							fill: "none",
						},
						...([ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(activity.status) && {
							"& svg .line": {
								fill: (theme) => (isLight ? theme.palette.grey["700"] : theme.palette.common.white),
							},
							"& svg .shadow": {
								fill: (theme) => (isLight ? theme.palette.grey["600"] : "#e5e8eb"),
							},
						}),
					}}
				>
					<TaskSvg />
					<Typography
						component="h3"
						title={activity.title}
						sx={{
							...([ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(activity.status) && {
								color: isLight ? "grey.700" : "common.white",
							}),
						}}
					>
						{activity.title}
					</Typography>
				</ActivityHeader>
				<Box
					display="flex"
					alignItems="center"
					justifyContent="space-between"
					gap="0.8rem"
					width="100%"
					color="grey.600"
				>
					<Box display="flex" alignItems="center" justifyContent="space-between" gap="0.8rem">
						<CalendarIcon
							type={activity.type}
							status={activity.status}
							useRemUnit
							svgColor={
								[ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(activity.status)
									? isLight
										? greyColors["700"]
										: theme.palette.primary["100"]
									: iconColor[activity.status]
							}
						/>
						<Typography
							component="span"
							fontSize="1.5rem"
							lineHeight={1.2}
							sx={{
								...([ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(activity.status) && {
									color: isLight ? "grey.700" : "common.white",
								}),
							}}
						>
							{formatMessage({
								id: "GLOBAL.date",
							})}
						</Typography>
					</Box>
					<Typography
						component="span"
						width="100%"
						textAlign="right"
						fontWeight={700}
						fontSize="1.5rem"
						sx={{
							...([ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(activity.status) && {
								color: isLight ? "grey.700" : "common.white",
							}),
						}}
					>
						{format(new Date(activity?.dueDate || activity?.dueAt), "MMM dd, yyyy")}
					</Typography>
				</Box>
				{!!activity.user && (
					<Box
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						gap="0.8rem"
						width="100%"
						color="grey.600"
					>
						<Box display="flex" alignItems="center" justifyContent="space-between" gap="0.8rem">
							<Person
								sx={{
									...([ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(activity.status)
										? {
												color: isLight ? greyColors["700"] : theme.palette.primary["100"],
										  }
										: {
												color: iconColor[activity.status],
										  }),
								}}
							/>
							<Typography
								component="span"
								fontSize="1.5rem"
								lineHeight={1.2}
								whiteSpace="nowrap"
								textOverflow="ellipsis"
								overflow="hidden"
								sx={{
									...([ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(
										activity.status,
									) && {
										color: isLight ? "grey.700" : "common.white",
									}),
								}}
							>
								{formatMessage({
									id: "AUTH.login.input.name.label",
								})}
							</Typography>
						</Box>
						<Typography
							component="span"
							width="100%"
							textAlign="right"
							fontWeight={700}
							fontSize="1.5rem"
							sx={{
								...([ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(activity.status) && {
									color: isLight ? "grey.700" : "common.white",
								}),
							}}
						>
							{activity.user.name}
						</Typography>
					</Box>
				)}
				{activity.status !== ActivityStatus.LOCKED && (
					<Box
						title={activity.description}
						sx={{
							width: "100%",
							fontWeight: 300,
							fontSize: "13px",
							color: [ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(activity.status)
								? isLight
									? "grey.600"
									: "common.white"
								: "grey.600",
							textOverflow: "ellipsis",
							overflow: "hidden",

							"& .communication-message": {
								display: "-webkit-box",
								WebkitLineClamp: activity.user ? 2 : 3,
								WebkitBoxOrient: "vertical",
								overflow: "hidden",
								textOverflow: "ellipsis",
								maxHeight: activity.user ? "40px" : "72px",

								"& *": {
									color: "inherit !important",
								},
							},
							"& .custom-variable": {
								fontWeight: 700,
							},
						}}
					>
						<div
							className="communication-message"
							dangerouslySetInnerHTML={{
								__html: activity.description as string,
							}}
						/>
					</Box>
				)}
			</Box>
			<Box
				display="flex"
				flexDirection="column"
				justifyContent="flex-end"
				gap="1.6rem"
				p="1.6rem"
				borderTop={(theme) =>
					`1px solid ${
						[ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(activity.status)
							? isLight
								? theme.palette.primary["400"]
								: theme.palette.primary["100"]
							: [ActivityStatus.OVERDUE, ActivityStatus.FAILED, ActivityStatus.REJECTED].includes(
									activity.status,
							  )
							? theme.palette.legacySecondary["200"]
							: activity.status === ActivityStatus.SOON
							? theme.palette.warning["200"]
							: activity.status === ActivityStatus.LOCKED
							? undefined
							: activity.status === ActivityStatus.COMPLETED
							? theme.palette.success["200"]
							: theme.palette.legacyPrimary["200"]
					}`
				}
				sx={{ width: "100%" }}
			>
				{activity.status === ActivityStatus.LOCKED ? (
					<LockedSection activity={activity} sx={{ m: "0 auto" }} />
				) : activity.status === ActivityStatus.COMPLETED ? (
					// Show finished text if activity is completed
					<Stack
						alignItems="center"
						justifyContent="center"
						p="1rem"
						pt="1.1rem"
						height="4.42rem"
						border={(theme) => "2px dashed " + theme.palette.success.main}
						borderRadius="8px"
					>
						<Typography
							color="success.main"
							textAlign="center"
							fontWeight={800}
							fontSize="1.6rem"
							letterSpacing="0.25px"
							lineHeight={1}
						>
							{formatMessage({
								id: "USER-JOURNEY.activity.card.status.completed",
							})}{" "}
							🎉
						</Typography>
					</Stack>
				) : (
					// Show start button if activity is not locked
					<ButtonContained
						data-useremunit="true"
						fullWidth
						sx={{
							p: "1.2rem",
							height: "4.42rem",
							letterSpacing: "0.25px",
						}}
						onClick={completeHandler}
						disabled={completeLoading}
					>
						{formatMessage({
							id: "DASHBOARD.employee-journey.activity_type.task.request-completion",
							defaultMessage: "Complete Task",
						})}
					</ButtonContained>
				)}
			</Box>
		</ActivityCard>
	);
};
